import { useContentPage } from '#imports'

/**
 * Try Nuxt Content first, fall back to Strapi.
 * Gated per-collection by `useContentMigrationFlag('<key>')` — when off, skips content and goes straight to Strapi.
 */

function toIsoDate(dayjs: ReturnType<typeof useDayjs>, date: string, time?: string): string {
    return dayjs(`${date}T${time || '00:00'}:00.000Z`).toISOString()
}

function normalizePublishAt(dayjs: ReturnType<typeof useDayjs>, publishAt?: { date?: string, time?: string }) {
    return publishAt?.date
        ? { publishAt: toIsoDate(dayjs, publishAt.date, publishAt.time) }
        : undefined
}

function normalizeMedia(media?: { url: string, thumbnailUrl?: string }) {
    if (!media) return undefined
    return {
        url: media.url,
        formats: {
            thumbnail: {
                url: media.thumbnailUrl || media.url,
            },
        },
    }
}

export function usePageData(seoName: string) {
    const { getPageBySeoName } = useContentPage()
    const strapi = useStrapi()
    const { site } = useCoreConfig()

    const contentQuery = useQuery(async () => {
        return await getPageBySeoName(seoName)
    }, {
        key: () => `content:page-${seoName}`,
        enabled: () => useContentMigrationFlag('pages'),
        lazy: false,
        server: true,
    })

    const strapiQuery = useQuery(async () => {
        const { data } = await strapi.GET('/pages/{seoName}', {
            params: { path: { seoName }, query: { brand: site.brand } },
        })
        return data
    }, {
        key: () => `strapi:page-${seoName}`,
        enabled: () => !useContentMigrationFlag('pages'),
        lazy: false,
        server: true,
    })

    return useContentMigrationFlag('pages') ? contentQuery : strapiQuery
}

export function useFaqData() {
    const { getFaqCollections } = useContentFaq()

    return useQuery(async () => {
        const collections = await getFaqCollections()
        const sections = collections?.[0]?.faqSections || []
        return sections.length ? sections : null
    }, {
        key: 'content:faq',
        enabled: () => useContentMigrationFlag('faq'),
        lazy: false,
        server: true,
    })
}

export function useConsentData(slug: string) {
    const { getConsentBySlug } = useContentConsent()
    const { getPrivacyPolicy } = useStrapiConsent()

    const contentQuery = useQuery(async () => {
        return await getConsentBySlug(slug)
    }, {
        key: () => `content:consent-${slug}`,
        enabled: () => useContentMigrationFlag('consents'),
        lazy: false,
        server: true,
    })

    const strapiQuery = getPrivacyPolicy({
        lazy: false,
        server: true,
        enabled: () => !useContentMigrationFlag('consents'),
    })

    return useContentMigrationFlag('consents') ? contentQuery : strapiQuery
}

export function useGeneralConsentData() {
    const { getGeneralConsents } = useContentGeneralConsent()
    const { getGeneralTerms } = useStrapiConsent()

    const contentQuery = useQuery(async () => {
        const consents = await getGeneralConsents()
        return consents?.length ? consents[0] : null
    }, {
        key: 'content:general-consent',
        enabled: () => useContentMigrationFlag('generalConsents'),
        lazy: false,
        server: true,
    })

    const strapiQuery = getGeneralTerms({
        lazy: false,
        server: true,
        enabled: () => !useContentMigrationFlag('generalConsents'),
    })

    return useContentMigrationFlag('generalConsents') ? contentQuery : strapiQuery
}

// TODO: Re-enable when blog content migration is ready
// export function useBlogData() { ... }
// export function useBlogPostData(slug: string) { ... }

export function usePressData() {
    const { getPressArticles } = useContentPress()

    return useQuery(async () => {
        const articles = await getPressArticles()
        if (!articles?.length) return null
        return articles.map(article => ({
            ...article,
            publishAt: article.publishAt
                ? { publishAt: article.publishAt }
                : undefined,
            media: normalizeMedia(article.media),
        }))
    }, {
        key: 'content:press',
        enabled: () => useContentMigrationFlag('press'),
        lazy: false,
        server: true,
    })
}

export function useGamePageData(seoName: string) {
    const { getGamePageBySeoName } = useContentGamePage()
    const strapi = useStrapi()
    const { site } = useCoreConfig()

    const contentQuery = useQuery(async () => {
        return await getGamePageBySeoName(seoName)
    }, {
        key: () => `content:game-page-${seoName}`,
        enabled: () => useContentMigrationFlag('gamePages'),
        lazy: false,
        server: true,
    })

    const strapiQuery = useQuery(async () => {
        const { data, error } = await strapi.GET('/game-pages/{seoName}', {
            params: { path: { seoName }, query: { brand: site.brand } },
        })
        if (error) throw error
        return data
    }, {
        key: () => `strapi:game-page-${seoName}`,
        enabled: () => !useContentMigrationFlag('gamePages'),
        lazy: false,
        server: true,
    })

    return useContentMigrationFlag('gamePages') ? contentQuery : strapiQuery
}

export function useGamePagesListData() {
    const { getGamePages } = useContentGamePage()
    const strapi = useStrapi()
    const { site } = useCoreConfig()

    const contentQuery = useQuery(async () => {
        return await getGamePages()
    }, {
        key: 'content:game-pages',
        enabled: () => useContentMigrationFlag('gamePages'),
        lazy: true,
        server: false,
    })

    const strapiQuery = useQuery(async () => {
        const { data, error } = await strapi.GET('/game-pages', {
            params: { query: { brand: site.brand } },
        })
        if (error) throw error
        if (data) {
            return [...data].sort((a, b) => (a.seoName || '').localeCompare(b.seoName || ''))
        }
        return []
    }, {
        key: 'strapi:game-pages',
        enabled: () => !useContentMigrationFlag('gamePages'),
        lazy: true,
        server: false,
    })

    return useContentMigrationFlag('gamePages') ? contentQuery : strapiQuery
}

export function useGamePageOverviewData() {
    const { getGamePageOverview } = useContentGamePage()
    const strapi = useStrapi()
    const { site } = useCoreConfig()

    const contentQuery = useQuery(async () => {
        return await getGamePageOverview()
    }, {
        key: 'content:game-page-overview',
        enabled: () => useContentMigrationFlag('gamePages'),
        lazy: false,
        server: true,
    })

    const strapiQuery = useQuery(async () => {
        const { data, error } = await strapi.GET('/game-pages/overview', {
            params: { query: { brand: site.brand } },
        })
        if (error) throw error
        return data
    }, {
        key: 'strapi:game-page-overview',
        enabled: () => !useContentMigrationFlag('gamePages'),
        lazy: false,
        server: true,
    })

    return useContentMigrationFlag('gamePages') ? contentQuery : strapiQuery
}

export function usePressArticleData(slug: string) {
    const { getPressArticleBySlug } = useContentPress()

    return useQuery(async () => {
        const article = await getPressArticleBySlug(slug)
        if (!article) return null
        return {
            ...article,
            publishAt: article.publishAt
                ? { publishAt: article.publishAt }
                : undefined,
            media: normalizeMedia(article.media),
        }
    }, {
        key: () => `content:press-article-${slug}`,
        enabled: () => useContentMigrationFlag('press'),
        lazy: false,
        server: true,
    })
}
